/**
 * What 3 Ingredients - Native Web Share & Clipboard Link Copy Handler
 * Provides 1-click recipe sharing across mobile (WhatsApp, iMessage, Twitter)
 * and desktop clipboard copy with toast notifications.
 */

import { encodeRecipeToURL } from './urlHandler.js';

/**
 * Triggers native share sheet or copies deep-link URL to clipboard
 * @param {object} recipe 
 * @param {number} servings 
 * @param {function} showToastFn 
 */
export async function shareRecipe(recipe, servings = 2, showToastFn) {
  if (!recipe) return;

  const shareUrl = encodeRecipeToURL(recipe, servings);
  const shareTitle = `Recipe: ${recipe.title}`;
  const shareText = `Check out this 3-ingredient recipe for ${recipe.title} on What 3 Ingredients!`;

  // Try native Web Share API first (Mobile browsers, Chrome, Safari)
  if (navigator.share) {
    try {
      await navigator.share({
        title: shareTitle,
        text: shareText,
        url: shareUrl
      });
      if (showToastFn) showToastFn('Shared successfully!');
      return;
    } catch (err) {
      // User cancelled share dialog or share failed
      if (err.name === 'AbortError') return;
      console.warn('Native share failed, falling back to clipboard copy', err);
    }
  }

  // Fallback: Copy link to clipboard
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(shareUrl);
    } else {
      // Legacy clipboard fallback
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }

    if (showToastFn) {
      showToastFn('Recipe link copied to clipboard!');
    }
  } catch (copyErr) {
    console.error('Could not copy link', copyErr);
    if (showToastFn) {
      showToastFn('Failed to copy link');
    }
  }
}
